import { Box, Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAtomValue, useSetAtom } from 'jotai';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { Delivery_Zone_Insert_Input, Minimum_Order_Value_Type_Enum } from '@kheops/graphql';
import { useInsertDeliveryZonesMutation } from '../../mutations/__generated__/insertDeliveryZones.generated';
import { UserRoleDocument } from '../../queries/__generated__/userRole.generated';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { useUpdateDeliveryZoneMutation } from '../../mutations/__generated__/updateDeliveryZone.generated';
import { currentContextAtom, signedContractsAtom } from '../../state';
import { ContractPreview } from '../../hooks/useContracts';
import { DeliveryZoneSetting } from './DeliveryZoneSetting';
import { SettingsDialogViewProps } from './SettingsDialog';

export interface DeliveryZoneSettingsProps extends SettingsDialogViewProps {
  indexZoneToModify?: number;
  addNewZone?: boolean;
  onUpdate?: (deliveryZonesContracts: ContractPreview[], deliveryZoneId: string) => void;
}

export interface DeliveryZoneFormValue {
  mov: number;
  areas: string[];
  mov_type: Minimum_Order_Value_Type_Enum;
  negotiableMinimumOrderValue: boolean;
  deliveryTime?: number | null;
}

export interface DeliveryZonesForm {
  deliveryZones: DeliveryZoneFormValue[];
}

const defaultDeliveryZone: DeliveryZoneFormValue = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mov: '' as any,
  areas: [],
  mov_type: Minimum_Order_Value_Type_Enum.Price,
  negotiableMinimumOrderValue: false,
};

export default function DeliveryZoneSettings({ next, onUpdate, standAlone, submitLabel, indexZoneToModify, addNewZone }: DeliveryZoneSettingsProps): React.JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  const context = useAtomValue(currentContextAtom);
  const signedContracts = useAtomValue(signedContractsAtom);
  const [insertDeliveryZones, { data: dataInsert, loading: loadingInsert }] = useInsertDeliveryZonesMutation({
    refetchQueries: [UserRoleDocument],
    awaitRefetchQueries: true,
  });
  const [updateDeliveryZone, { data: dataUpdate, loading: loadingUpdate }] = useUpdateDeliveryZoneMutation({
    refetchQueries: [UserRoleDocument],
  });
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);

  let deliveryZonesDefault = context.deliveryZones?.map((zone) => ({
    mov: zone.minimum_order_value,
    areas: zone.administrative_areas.map(({ area }) => area),
    mov_type: zone.minimum_order_value_type,
    negotiableMinimumOrderValue: zone.negotiable_minimum_order_value,
    deliveryTime: zone.delivery_time,
  })) || [{ ...defaultDeliveryZone }];

  if (addNewZone) {
    deliveryZonesDefault.push({
      ...defaultDeliveryZone,
      mov_type: context.deliveryZones?.[0]?.minimum_order_value_type || Minimum_Order_Value_Type_Enum.Price,
    });
  }

  if (!deliveryZonesDefault.length) {
    deliveryZonesDefault = [{ ...defaultDeliveryZone }];
  }

  const form = useForm<DeliveryZonesForm>({
    defaultValues: {
      deliveryZones: deliveryZonesDefault,
    },
    mode: 'onChange',
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'deliveryZones',
  });

  const isValidButtonDisabled = useMemo((): boolean => {
    return !form.formState.isDirty || !form.formState.isValid;
  }, [form.formState.isValid, form.formState.isDirty]);

  const handleSubmit = useCallback(() => {
    const { deliveryZones } = form.getValues();

    if (indexZoneToModify !== undefined) {
      const zoneToSave = deliveryZones[indexZoneToModify];
      const previousZoneValue = context.deliveryZones![indexZoneToModify];
      const idZone = previousZoneValue.id;

      updateDeliveryZone({
        variables: {
          id: idZone,
          deliveryZone: {
            minimum_order_value: Number(zoneToSave.mov),
            minimum_order_value_type: zoneToSave.mov_type,
            negotiable_minimum_order_value: zoneToSave.negotiableMinimumOrderValue,
            delivery_time: zoneToSave.deliveryTime,
          },
          newAreas: zoneToSave.areas.map((area) => ({ area, delivery_zone_id: idZone, country_code: 'FR' })),
          previousAreas: context.deliveryZones![indexZoneToModify].administrative_areas.map(({ area }) => area),
        },
      });

      return;
    }

    let zoneToSave = deliveryZones;

    if (addNewZone) {
      zoneToSave = [deliveryZones[deliveryZones.length - 1]];
    }

    const formattedZoneToSave = zoneToSave.map<Delivery_Zone_Insert_Input>((zone) => ({
      minimum_order_value: Number(zone.mov),
      minimum_order_value_type: zone.mov_type,
      negotiable_minimum_order_value: zone.negotiableMinimumOrderValue,
      delivery_time: zone.deliveryTime,
      company_id: context.companyId,
      administrative_areas: {
        data: zone.areas.map((area) => ({ area, country_code: 'FR' })),
      },
    }));

    insertDeliveryZones({ variables: { delivery_zones: formattedZoneToSave } });
  }, [context, form]);

  useEffect((): void => {
    if ((dataInsert?.insert_delivery_zone?.affected_rows || dataUpdate?.update_delivery_zone_by_pk)) {
      if (standAlone) {
        const newDeliveryZone = (dataInsert?.insert_delivery_zone?.returning[0] || dataUpdate?.update_delivery_zone_by_pk)!;
        const newAreas = (dataInsert?.insert_delivery_zone?.returning[0].administrative_areas || dataUpdate?.insert_delivery_zone_administrative_area?.returning)!.map(({ area }) => area);

        setCommonSnackbarProps({
          label: t('settings:your_profile_has_been_updated'),
          snackbarProps: {
            open: true,
          },
        });

        let deliveryZoneContracts: ContractPreview[];

        if (newAreas?.[0] === 'ALL_AREAS') {
          if (context.deliveryZones!.length === 1) {
            deliveryZoneContracts = signedContracts;
          } else {
            const existingAreas = context.deliveryZones!.flatMap(({ administrative_areas }) => administrative_areas.map(({ area }) => area));

            deliveryZoneContracts = signedContracts.filter(({ company }) => !existingAreas.includes(company.addresses[0].administrativeAreaLevel2!));
          }
        } else {
          deliveryZoneContracts = signedContracts.filter(({ company }) => newAreas?.includes(company.addresses[0].administrativeAreaLevel2!));
        }

        deliveryZoneContracts = deliveryZoneContracts.filter(({ minimum_order_value, minimum_order_value_type }) =>
          newDeliveryZone.minimum_order_value !== minimum_order_value || newDeliveryZone.minimum_order_value_type !== minimum_order_value_type);

        if (onUpdate) {
          onUpdate(deliveryZoneContracts, newDeliveryZone.id);
        } else {
          next();
        }
      } else {
        next();
      }
    }
  }, [next, dataInsert, dataUpdate]);

  return (
    <FormProvider {...form}>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <DialogTitle>
          <Typography>{t('settings:your_delivery_zones')}</Typography>
          <Typography>
            {t('settings:your_delivery_zones_description')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Box sx={{ overflow: 'auto' }}>
            {fields.map((field, index) => {
              const canEditField = (!addNewZone && (indexZoneToModify === undefined || indexZoneToModify === index))
                || (addNewZone && index === fields.length - 1);

              if (canEditField) {
                return (
                  <DeliveryZoneSetting
                    key={field.id}
                    index={index}
                    deletable={fields.length > 1}
                    deleteZone={() => remove(index)}
                    standAlone={standAlone}
                  />
                );
              }

              return undefined;
            })}
          </Box>
          {!standAlone && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                alignItems: 'center',
                backgroundColor: 'surfaceContainerLow.main',
                borderRadius: 3,
                py: 1.5,
                mt: 2,
              }}
            >
              <Typography variant="titleMedium">
                {t('settings:you_have_more_than_one_delivery_zone')}
              </Typography>
              <Button
                onClick={() => append({ ...defaultDeliveryZone })}
                variant="outlined"
                startIcon={<AddIcon />}
              >
                {t('settings:add_new_delivery_zone')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {
            standAlone
              ? (
                <Button onClick={() => next()} variant="text">
                  {t('common:cancel')}
                </Button>
              )
              : (
                <Button onClick={() => next({ skipped: true })} variant="outlined">
                  {t('settings:complete_later')}
                </Button>
              )
          }
          <Button
            onClick={handleSubmit}
            loading={loadingInsert || loadingUpdate}
            variant="contained"
            disabled={isValidButtonDisabled}
          >
            {submitLabel || t('common:validate')}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
}
